import type { SoulMapDocumentStatus } from '@/services/practice-service';
import { useAuthStore } from '@/store/use-auth-store';
import { getActiveTenantId } from '@/utils/active-tenant';

const SOUL_MAP_DOWNLOAD_PROXY_PATH = '/__soulmap-download';
const API_VERSION_SEGMENT = '/api/v1';

export class SoulMapDownloadError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'SoulMapDownloadError';
    this.status = status;
  }
}

export interface SoulMapDocumentDownloadOptions {
  practiceId?: number;
  documentStatus?: SoulMapDocumentStatus | null;
}

export type SoulMapDocumentDownloadResult =
  | { mode: 'blob'; blob: Blob; filename: string }
  | { mode: 'opened'; filename: string };

function getRequestTenantId(): number | null {
  const { tenants } = useAuthStore.getState();
  return getActiveTenantId() ?? tenants?.[0]?.id ?? null;
}

function isPasswordLogin(): boolean {
  return useAuthStore.getState().authMethod === 'password';
}

/** Map backend absolute download URLs onto the configured API client base URL. */
export function toSoulMapDownloadApiPath(downloadUrl: string): string {
  if (!downloadUrl) {
    return downloadUrl;
  }

  if (downloadUrl.startsWith('/agentos/')) {
    return downloadUrl;
  }

  try {
    const parsed = new URL(downloadUrl);
    const apiIndex = parsed.pathname.indexOf(API_VERSION_SEGMENT);
    if (apiIndex >= 0) {
      return `${parsed.pathname.slice(apiIndex + API_VERSION_SEGMENT.length)}${parsed.search}`;
    }

    if (parsed.pathname.startsWith('/agentos/')) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Fall through for malformed URLs.
  }

  return downloadUrl;
}

export function resolveDownloadUrl(downloadUrl: string): string {
  if (!downloadUrl) {
    return downloadUrl;
  }

  if (downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://')) {
    try {
      const parsed = new URL(downloadUrl);
      const apiIndex = parsed.pathname.indexOf(API_VERSION_SEGMENT);
      if (apiIndex >= 0) {
        const configuredBase =
          import.meta.env.VITE_ZENAPI_BASE_URL ||
          'https://api.prod.mentoralift.ai/api/v1';
        const apiPath = parsed.pathname.slice(
          apiIndex + API_VERSION_SEGMENT.length
        );
        return `${configuredBase.replace(/\/$/, '')}${apiPath}${parsed.search}`;
      }
      return downloadUrl.replace(/^http:\/\//i, 'https://');
    } catch {
      return downloadUrl;
    }
  }

  const configuredBase =
    import.meta.env.VITE_ZENAPI_BASE_URL ||
    'https://api.prod.mentoralift.ai/api/v1';
  return `${configuredBase.replace(/\/$/, '')}${toSoulMapDownloadApiPath(downloadUrl)}`;
}

function stripTenantIdQuery(urlOrPath: string): string {
  try {
    const isAbsolute =
      urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://');
    const parsed = isAbsolute
      ? new URL(urlOrPath)
      : new URL(urlOrPath, 'https://placeholder.local');
    parsed.searchParams.delete('tenant_id');
    return isAbsolute ? parsed.href : `${parsed.pathname}${parsed.search}`;
  } catch {
    return urlOrPath;
  }
}

export function isPresignedObjectStoreUrl(url: string): boolean {
  if (!url) {
    return false;
  }

  try {
    const parsed = new URL(url);
    return (
      parsed.hostname.includes('amazonaws.com') ||
      parsed.hostname.endsWith('.cloudfront.net')
    );
  } catch {
    return false;
  }
}

export function getFilenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string
): string {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].trim());
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1].trim();
  }

  return fallback;
}

export function getFilenameFromRedirectUrl(
  redirectUrl: string,
  fallback: string
): string {
  try {
    const parsed = new URL(redirectUrl);
    const disposition = parsed.searchParams.get('response-content-disposition');
    if (disposition) {
      return getFilenameFromContentDisposition(disposition, fallback);
    }
  } catch {
    // Fall through to fallback filename.
  }

  return fallback;
}

function pushUrl(urls: string[], value: unknown): void {
  if (typeof value === 'string' && value.trim()) {
    urls.push(value.trim());
  }
}

export function collectUrlsFromDocumentStatus(
  status: SoulMapDocumentStatus | null | undefined
): string[] {
  if (!status) {
    return [];
  }

  const urls: string[] = [];

  pushUrl(urls, status.downloads?.shareable);
  pushUrl(urls, status.downloads?.personal);

  for (const document of Object.values(status.documents ?? {})) {
    for (const format of Object.values(document ?? {})) {
      pushUrl(urls, format?.url);
    }
  }

  for (const formats of Object.values(status.formats ?? {})) {
    for (const url of Object.values(formats ?? {})) {
      pushUrl(urls, url);
    }
  }

  return urls;
}

function normalizeUrlKey(url: string): string {
  try {
    const parsed = new URL(resolveDownloadUrl(url));
    return parsed.pathname.replace(/\/+$/, '');
  } catch {
    return url.replace(/\/+$/, '');
  }
}

function openFileViaNavigation(
  url: string,
  fallbackFilename: string
): SoulMapDocumentDownloadResult {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();

  return {
    mode: 'opened',
    filename: getFilenameFromRedirectUrl(url, fallbackFilename),
  };
}

function buildAuthenticatedNavigationUrl(
  downloadUrl: string,
  tenantId: number
): string {
  const url = new URL(resolveDownloadUrl(downloadUrl));
  url.searchParams.set('tenant_id', String(tenantId));
  return url.href;
}

function findPresignedDownloadUrl(candidates: string[]): string | null {
  const seen = new Set<string>();

  for (const candidate of candidates) {
    const resolved = resolveDownloadUrl(candidate);
    if (seen.has(resolved)) {
      continue;
    }
    seen.add(resolved);

    if (isPresignedObjectStoreUrl(resolved)) {
      return resolved;
    }
  }

  return null;
}

function findPresignedUrlForReference(
  referenceUrl: string,
  candidates: string[]
): string | null {
  const referenceKey = normalizeUrlKey(referenceUrl);

  for (const candidate of candidates) {
    const resolved = resolveDownloadUrl(candidate);
    if (
      normalizeUrlKey(candidate) === referenceKey ||
      normalizeUrlKey(resolved) === referenceKey
    ) {
      if (isPresignedObjectStoreUrl(resolved)) {
        return resolved;
      }
    }
  }

  return findPresignedDownloadUrl(candidates);
}

function gatherDownloadCandidates(
  downloadUrl: string,
  options?: SoulMapDocumentDownloadOptions
): string[] {
  const candidates = [downloadUrl];

  if (options?.documentStatus) {
    candidates.push(...collectUrlsFromDocumentStatus(options.documentStatus));
  }

  return candidates;
}

function getDetailFromUnknown(value: unknown): string | null {
  if (!value || typeof value !== 'object' || !('detail' in value)) {
    return null;
  }

  const detail = (value as { detail: unknown }).detail;
  return typeof detail === 'string' && detail.trim() ? detail : null;
}

async function errorFromFailedResponse(
  response: Response
): Promise<SoulMapDownloadError> {
  let detail = 'Could not download your Soul Map PDF. Please try again.';
  try {
    const contentType = response.headers.get('content-type') ?? '';
    const payload: unknown = contentType.includes('application/json')
      ? await response.json()
      : await response.text();
    detail =
      getDetailFromUnknown(payload) ??
      (typeof payload === 'string' && payload.trim() ? payload : detail);
  } catch {
    // Keep the generic message when the body is not readable.
  }

  return new SoulMapDownloadError(detail, response.status);
}

async function downloadViaProxy(
  requestUrl: string,
  fallbackFilename: string,
  tenantId: number
): Promise<SoulMapDocumentDownloadResult> {
  const response = await fetch(
    `${SOUL_MAP_DOWNLOAD_PROXY_PATH}?url=${encodeURIComponent(requestUrl)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'x-tenant-id': String(tenantId),
      },
    }
  );

  if (!response.ok) {
    throw await errorFromFailedResponse(response);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('text/html')) {
    throw new SoulMapDownloadError(
      'Could not download your Soul Map PDF. Please try again.'
    );
  }

  return {
    mode: 'blob',
    blob: await response.blob(),
    filename: getFilenameFromContentDisposition(
      response.headers.get('content-disposition') ?? undefined,
      fallbackFilename
    ),
  };
}

export function getSoulMapDownloadErrorMessage(error: unknown): string {
  if (error instanceof SoulMapDownloadError) {
    return error.message;
  }

  return 'Could not download your Soul Map PDF. Please try again.';
}

/**
 * SSO already works by opening the API download URL (cookies + ?tenant_id=).
 * Password sessions use /__soulmap-download in every environment. Login also
 * copies the session cookie onto this app host so the proxy can authenticate.
 */
export async function downloadSoulMapDocumentFile(
  downloadUrl: string,
  fallbackFilename: string,
  options?: SoulMapDocumentDownloadOptions
): Promise<SoulMapDocumentDownloadResult> {
  const tenantId = getRequestTenantId();
  if (tenantId == null) {
    throw new SoulMapDownloadError(
      'Could not determine your organization. Refresh the page and try again.'
    );
  }

  let resolvedReference: string;
  try {
    resolvedReference = resolveDownloadUrl(downloadUrl);
  } catch {
    throw new SoulMapDownloadError('Invalid download link. Please try again.');
  }

  if (isPresignedObjectStoreUrl(resolvedReference)) {
    return openFileViaNavigation(
      resolvedReference,
      getFilenameFromRedirectUrl(resolvedReference, fallbackFilename)
    );
  }

  const candidates = gatherDownloadCandidates(downloadUrl, options);
  const presignedUrl = findPresignedUrlForReference(downloadUrl, candidates);

  if (presignedUrl) {
    return openFileViaNavigation(
      presignedUrl,
      getFilenameFromRedirectUrl(presignedUrl, fallbackFilename)
    );
  }

  if (isPasswordLogin()) {
    return downloadViaProxy(
      stripTenantIdQuery(resolvedReference),
      fallbackFilename,
      tenantId
    );
  }

  return openFileViaNavigation(
    buildAuthenticatedNavigationUrl(downloadUrl, tenantId),
    fallbackFilename
  );
}
